/**
 * AlertBlock.tsx
 *
 * [2024-01-07.1401 by Brian]
 *    Just a way to put an alert message in a box consistently.
 *    A bunch of sane defaults with the ability to override them
 *    selectively. Probably, the less we override, the better.
 *
 *    Usage:
 *
 *      <AlertBock message="This is the message." />
 *
 *    Will render this in a red box with default settings:
 *
 *      Warning! This is the message.
 *
 *    Alternative usage with children:
 *
 *      <AlertBlock level="note">
 *        This is the message as children.
 *      </AlertBlock>
 *
 *  children: React.ReactNode
 *     Supports children for more complex content. If both children
 *     and message are provided, children takes priority.
 *
 *  omitWhenEmpty: boolean
 *     If there is no message, this won't render anything. Set this
 *     to false if you want the box to render when empty.
 *
 *  closeCallback: () => void
 *     A function returning void. If provided, a close button will
 *     appear and clicking it will execute the callback. Clearing
 *     out the message will close the alert.
 */
import { cn } from '@/lib/utils';
import {
  BusinessRuleItemType,
  STRING_ANY_HASH,
  STRING_STRING_HASH,
} from '@chiroup/core/constants/globals';
import { XCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

type Props = {
  level?: string;
  margin?: string;
  padding?: string;
  text?: string;
  rounded?: string;
  role?: string;
  intro?: React.ReactNode;
  message?: React.ReactNode;
  omitWhenEmpty?: boolean;
  issues?: BusinessRuleItemType[];
  closeCallback?: () => void;
  emptyComponent?: React.ReactNode;
  extraClassName?: string;
  children?: React.ReactNode;
};

const levels: STRING_STRING_HASH = {
    default: 'text-red-800 bg-red-50 dark:bg-gray-800 dark:text-red-400',
    note: 'text-blue-800 bg-blue-50 dark:bg-blue-800 dark:text-blue-400',
  },
  intros: STRING_ANY_HASH = {
    none: (
      <>
        <span className="font-medium"></span>
        {`   `}
      </>
    ),
    note: (
      <>
        <span className="font-medium">Note:</span>
        {`   `}
      </>
    ),
    error: (
      <>
        <span className="font-medium">Error:</span>
        {`   `}
      </>
    ),
    caution: (
      <>
        <span className="font-medium">Caution:</span>
        {`   `}
      </>
    ),
    default: (
      <>
        <span className="font-medium">Warning!</span>
        {`   `}
      </>
    ),
  };

export const AlertBlock: React.FC<Props> = ({
  level = 'warn',
  message = null,
  margin = 'my-4',
  padding = 'p-4',
  rounded = 'rounded-lg',
  text = 'text-sm',
  role = 'alert',
  extraClassName = '',
  intro = intros[level] ?? intros.default,
  omitWhenEmpty = true,
  closeCallback,
  issues = null,
  emptyComponent = null,
  children,
}) => {
  const showComponent = React.useMemo(() => {
    if (issues) {
      if (issues.length === 0) return emptyComponent;

      return issues.map(rule => rule.text).join(' ');
    }

    // Prioritize children over message for content
    const content = children ?? message;

    if (omitWhenEmpty && !content) return emptyComponent;

    return content;
  }, [issues, emptyComponent, message, omitWhenEmpty, children]);

  if (!showComponent || showComponent === emptyComponent) return showComponent;

  return (
    <div
      className={cn(
        margin,
        padding,
        text,
        rounded,
        levels[level] ?? levels.default,
        extraClassName,
        'relative'
      )}
      role={role}
    >
      {intro}
      {showComponent}
      {closeCallback && (
        <button
          className="absolute top-1 right-1 text-red-800 dark:text-red-400"
          onClick={closeCallback}
        >
          <XCircleIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
